// NewProjectModal.jsx
// ============================================================================
// "+ Projects" → this popup. Type a name (and optionally describe it in plain
// words), and Aria classifies what kind of project/business it is — locally via
// the backend's Ollama (POST /api/aria/classify-project). If the AI is
// unavailable, a keyword heuristic covers the common trades so a project always
// gets created. Returns a card-shaped project via onCreate().
// ============================================================================

import React, { useState } from 'react';
import { getApiBase, fetchWithTimeout } from './apiBase';

const API_BASE = getApiBase();
const FALLBACK_IMG = 'https://images.unsplash.com/photo-1559028012-481c04fa702d?w=520&h=320&fit=crop&q=80';

// Client-side fallback if the local model is unreachable. Covers the trades
// Braeden cares about + common project types.
function heuristic(name, desc) {
  const t = (name + ' ' + desc).toLowerCase();
  const rules = [
    [/junk|haul|debris|removal|cleanout|dump/, 'Junk removal', 'business'],
    [/plumb/, 'Plumbing', 'business'],
    [/hvac|heating|cooling|furnace|\bac\b|air condition/, 'Heating & cooling', 'business'],
    [/electric/, 'Electrical', 'business'],
    [/landscap|lawn|yard|garden|tree/, 'Landscaping', 'business'],
    [/clean|maid|janitor/, 'Cleaning service', 'business'],
    [/roof/, 'Roofing', 'business'],
    [/paint/, 'Painting', 'business'],
    [/concrete|mason|drywall|carpent|contractor|construct/, 'Contracting', 'business'],
    [/mechanic|auto|car repair|detailing/, 'Auto service', 'business'],
    [/book|novel|memoir|write|author|screenplay/, 'Writing', 'creative'],
    [/website|portfolio|\.com|blog|landing/, 'Website', 'personal'],
    [/campaign|marketing|launch|\bads?\b|promo/, 'Marketing', 'campaign'],
    [/reno|remodel|kitchen|bath|flip/, 'Renovation', 'project'],
  ];
  for (const [re, tag, category] of rules) if (re.test(t)) return { tag, category };
  return { tag: 'Project', category: 'project' };
}

const overlay = { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9000, padding: 20 };
const modal = { background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 16, width: '100%', maxWidth: 440, padding: '22px 24px', color: 'var(--text)', fontFamily: 'inherit' };
const lbl = { display: 'block', fontSize: 12, color: 'var(--text-muted)', margin: '0 0 5px' };
const inp = { width: '100%', background: 'var(--input-bg)', border: '1px solid var(--border)', color: 'var(--text)', borderRadius: 9, padding: '10px 12px', fontSize: 14, fontFamily: 'inherit', outline: 'none', marginBottom: 14, boxSizing: 'border-box' };
const btnGhost = { background: 'transparent', color: 'var(--text-muted)', border: 0, fontSize: 14, cursor: 'pointer', fontFamily: 'inherit' };
const btnPrimary = { background: 'var(--accent)', color: '#06210f', border: 0, borderRadius: 999, padding: '9px 20px', fontSize: 14, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' };

export default function NewProjectModal({ onClose, onCreate }) {
  const [name, setName] = useState('');
  const [desc, setDesc] = useState('');
  const [busy, setBusy] = useState(false);

  const create = async () => {
    const nm = name.trim();
    if (!nm || busy) return;
    setBusy(true);
    let cls = null;
    try {
      const r = await fetchWithTimeout(`${API_BASE}/api/aria/classify-project`, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ name: nm, description: desc.trim() }),
        timeout: 15000,
      });
      const d = await r.json();
      if (d && d.ok && (d.kind || d.tag)) {
        // Prefer the KIND (the type, e.g. "Plumbing") for the label; fall back
        // to the tag. Avoids the model echoing the business's proper name.
        cls = { tag: String(d.kind || d.tag), category: String(d.category || 'project') };
      }
    } catch (_) { /* fall back to heuristic */ }
    if (!cls) cls = heuristic(nm, desc);

    onCreate({
      name: nm,
      tag: cls.tag,
      sub: desc.trim() || `${cls.tag} · ${cls.category}`,
      status: 'New',
      activity: 'Just added',
      img: '/cards/business.jpg',
      fallback: FALLBACK_IMG,
    });
    setBusy(false);
  };

  return (
    <div style={overlay} onClick={onClose} role="presentation">
      <div style={modal} onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true" aria-label="New project">
        <div style={{ fontSize: 18, fontWeight: 600, marginBottom: 4 }}>New project</div>
        <div style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 16 }}>Name it and tell me what it is — I'll figure out the type for you.</div>
        <label style={lbl}>Name</label>
        <input
          autoFocus
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="e.g. Gilbert Junk Removal"
          style={inp}
          onKeyDown={(e) => { if (e.key === 'Enter') create(); }}
        />
        <label style={lbl}>What's it for? (optional)</label>
        <textarea
          value={desc}
          onChange={(e) => setDesc(e.target.value)}
          rows={3}
          placeholder="Type anything — a business, a side hustle, a book… Aria will classify it."
          style={{ ...inp, resize: 'vertical' }}
        />
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 14, marginTop: 6 }}>
          <button type="button" onClick={onClose} style={btnGhost} disabled={busy}>Cancel</button>
          <button type="button" onClick={create} style={btnPrimary} disabled={busy || !name.trim()}>{busy ? 'Classifying…' : 'Create'}</button>
        </div>
      </div>
    </div>
  );
}
